import React, { useCallback, useEffect, useState } from 'react';
import { Container, Typography, Paper, List, ListItem, ListItemText, IconButton, CircularProgress, Button } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { fetchCustomers } from '../api';
import { useTranslation } from 'react-i18next';

const CustomersPage = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [customers, setCustomers] = useState([]);

  const loadCustomers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchCustomers();
      // Expect data to be an array of customer objects
      setCustomers(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to load customers', err);
      setError(t('customersPage.errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadCustomers();
  }, [loadCustomers]);

  const openLogin = (customer) => {
    if (!customer || !customer.token) return;
    const base = customer.domain || window.location.origin;
    const url = `${base.replace(/\/+$/, '')}/?token=${encodeURIComponent(customer.token)}`;
    window.open(url, '_blank', 'noopener');
  };

  const copyLogin = async (customer) => {
    if (!customer || !customer.token) return;
    const base = customer.domain || window.location.origin;
    const url = `${base.replace(/\/+$/, '')}/?token=${encodeURIComponent(customer.token)}`;
    try {
      await navigator.clipboard.writeText(url);
    } catch (e) {
      console.error('Failed to copy', e);
    }
  };

  return (
    <Container>
      <Typography variant="h4" sx={{ mb: 2, mt: 2 }}>{t('customersPage.title')}</Typography>

      <Paper sx={{ p: 2 }}>
        <List>
          {loading && (
            <ListItem>
              <CircularProgress />
            </ListItem>
          )}

          {error && (
            <ListItem>
              <ListItemText primary={error} />
            </ListItem>
          )}

          {!loading && customers.length === 0 && !error && (
            <ListItem>
              <ListItemText primary={t('customersPage.empty')} />
            </ListItem>
          )}

          {customers.map((c) => (
            <ListItem key={c.id || c.name || c.domain} secondaryAction={
              <>
                {c.token ? (
                  <>
                    <Button variant="outlined" size="small" onClick={() => openLogin(c)} startIcon={<OpenInNewIcon />} sx={{ mr: 1 }}>
                      {t('customersPage.actions.login')}
                    </Button>
                    <IconButton edge="end" aria-label={t('customersPage.actions.copy')} onClick={() => copyLogin(c)}>
                      <ContentCopyIcon />
                    </IconButton>
                  </>
                ) : (
                  <Button
                    variant="outlined"
                    size="small"
                    component="a"
                    href={`/token.html?customer_id=${encodeURIComponent(c.id)}`}
                    // open in same tab; add noopener for safety when opening new tab
                  >
                    {t('customersPage.actions.tokenPage')}
                  </Button>
                )}
              </>
            }>
              <ListItemText
                primary={c.name || c.id || c.domain}
                secondary={c.email || c.domain || null}
              />
            </ListItem>
          ))}
        </List>
      </Paper>
    </Container>
  );
};

export default CustomersPage;
